import React, { useState, useEffect } from 'react';
import { Typography, Divider, Modal, Spacer } from '@telus-uds/components-web';
import PropTypes from 'prop-types';
import _ from 'lodash';
import axios from 'axios';
import { Box } from '../../design/css';
import { OrderDetailsBox } from '../../defectManagement/css';
import { OrderSummaryMain } from './css';
import { getLocale } from '../../misc';
import {
  contentfulFormConfig,
  getContentfulData,
  getData
} from '../../contentful';
import {
  productValuesToBackendKeys,
  keyOrderSummaryForm,
  keyOrderSummary
} from '../../utils/constant';
import EN from '../../../EN.json';
import FR from '../../../FR.json';

import { apiHost, config as axiosConfig } from '../../utils/request';
import ViewOrderHeader from './viewOrderHeader';
import {
  OrderHistory,
  Comments,
  OrderButton
} from '../dashboardCommon/components';
import Attachements from './components/attachments';
import OrderSummary from '../../form/orderSummary/cybersecurityOrderSummary';
import StickyOrderModalHeader from '../dashboardCommon/StickyOrderModalHeader';

// CS orders are submitted from the webform; this dashboard view is read-only (order summary + comments).
const ViewOrder = ({ orderData, open, setOpen, onSave }) => {
  const locale = getLocale();
  const config = { ...contentfulFormConfig, locale };

  const {
    ORDERS: { ORDER_SUMMARY, save }
  } = locale === 'en-US' ? EN : FR;

  const [progressBarSteps, setProgressBarSteps] = useState();
  const [comment, setComment] = useState('');
  const [addingComment, setAddingComment] = useState(false);
  const [addNewComment, setAddNewComment] = useState(false);
  const isOrderUpdated = !!comment;

  const closeModal = needToClose => {
    if (isOrderUpdated) {
      if (onSave) {
        onSave({ isUpdated: true, orderId: orderData.orderId });
      }
      if (needToClose) setOpen(!open);
    }
  };

  const onAddComment = async () => {
    setAddingComment(true);
    await axios
      .post(
        `${apiHost}/orders/${orderData.id}/comments`,
        { text: comment },
        axiosConfig()
      )
      .catch(() => {})
      .finally(() => {
        setAddingComment(false);
      });
  };

  const onSaveClick = async (needToClose = true) => {
    if (comment) await onAddComment();
    closeModal(needToClose);
  };

  const updateProgressBarSteps = (productSelection, steps) => {
    let result = steps.filter(item =>
      ['quoteCybersecurity', 'additionalCustomerInformationCS'].includes(
        item.fields.backendKey
      )
    );
    if (productSelection && productSelection.length) {
      result = [
        ...result,
        ...steps.filter(stepItem =>
          productSelection.some(
            item =>
              productValuesToBackendKeys[item] === stepItem.fields.backendKey
          )
        )
      ];
    }

    result = [
      ...result,
      ...steps.filter(item =>
        [keyOrderSummaryForm, keyOrderSummary].includes(item.fields.backendKey)
      )
    ];

    setProgressBarSteps(result);
  };

  useEffect(() => {
    getContentfulData(config).then(res => {
      const { formCategories } = getData(res);

      const categoryCS = formCategories.find(
        category => _.get(category, 'fields.name', '') === 'cybersecurity'
      );

      const csOrderSummaryFrom = _.get(categoryCS, 'fields.forms', []).find(
        form => _.get(form, 'fields.name', '') === 'Cybersecurity Form'
      );

      updateProgressBarSteps(
        orderData.productSelectionCS,
        _.get(csOrderSummaryFrom, 'fields.steps', [])
      );
    });
  }, []);

  const buttonSaveComment = {
    label: save,
    onClick: onSaveClick,
    variant: 'purple'
  };

  return (
    <div id="modal">
      {open && <StickyOrderModalHeader />}
      <Modal
        isOpen={open}
        onClose={setOpen}
        heading={orderData.orderId}
        variant={{ width: 'xl' }}
        tokens={{ maxWidth: 1024 }}
      >
        <Spacer space={4} />
        <OrderDetailsBox>
          <ViewOrderHeader orderData={orderData} />
        </OrderDetailsBox>
        <Typography heading="h2" variant={{ size: 'h2' }} block>
          {ORDER_SUMMARY}
        </Typography>
        <Spacer space={2} />
        <OrderSummaryMain>
          <OrderSummary stepData={progressBarSteps} orderData={orderData} />
        </OrderSummaryMain>
        <OrderHistory orderId={orderData.id} />
        <Comments
          comment={comment}
          setComment={setComment}
          orderId={orderData.id}
          activeButton={setAddNewComment}
        />
        <Box
          mt={20}
          display="flex"
          justifyContent="center"
          width="100"
          style={{ alignItems: 'center' }}
        >
          <OrderButton
            button={{ ...buttonSaveComment, label: 'Save' }}
            isOrderValidating={addingComment}
            actionChanged={addNewComment}
          />
        </Box>
        <Attachements orderData={orderData} />
        <Divider space={2} variant={{ inverse: true }} />
      </Modal>
    </div>
  );
};

ViewOrder.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  onSave: PropTypes.func,
  orderData: PropTypes.shape({
    orderId: PropTypes.string,
    id: PropTypes.number,
    status: PropTypes.string,
    productSelectionCS: PropTypes.arrayOf(PropTypes.string),
    emailAddressCS: PropTypes.string
  }).isRequired
};
export default ViewOrder;
